import cs from "classnames";
import React from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";

import { normalizeSearchString } from "src/common/utils/common";
import { scrollIntoView } from "src/common/utils/dom";
import { genericMessages } from "src/messages/generic";

import "./Select.css";

const defaultOptions: Option[] = [];
const noop = (): void => {};

interface SelectProps {
	disabled?: boolean;
	placeholder?: string;
	canClear?: boolean;
	options?: Option[];
	value?: string | undefined;
	onChangeValue?(value: string | undefined): unknown;
}

export interface Option {
	value: string;
	label: string;
	disabled?: boolean;
}

export const Select: React.FunctionComponent<SelectProps> = ({
	disabled,
	placeholder,
	canClear = true,
	options = defaultOptions,
	value,
	onChangeValue = noop
}) => {
	const { formatMessage } = useIntl();

	const containerRef = React.useRef<HTMLDivElement>(null);
	const queryRef = React.useRef<HTMLInputElement>(null);

	const [{ focused, open, query, index }, setState] = React.useState({
		focused: false,
		open: false,
		query: "",
		index: 0
	});

	const finalPlaceholder = React.useMemo(
		() => placeholder || formatMessage(genericMessages.pleaseSelect),
		[placeholder, formatMessage]
	);

	const selectedLabel = React.useMemo(() => {
		if (value === undefined) {
			return finalPlaceholder;
		}
		const option = options.find(option => option.value === value);
		if (option === undefined) {
			return finalPlaceholder;
		}
		return option.label;
	}, [value, finalPlaceholder, options]);

	const filteredOptions = React.useMemo(() => {
		const normalizedQuery = normalizeSearchString(query);
		return options.filter(option =>
			normalizeSearchString(option.label).includes(normalizedQuery)
		);
	}, [options, query]);

	const enabledIndexes = React.useMemo(
		() =>
			filteredOptions
				.map((_option, index) => index)
				.filter(index => !filteredOptions[index].disabled),
		[filteredOptions]
	);

	const onClickContainer = React.useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setState(state => ({ ...state, focused: true, open: true }));
	}, []);

	const onFocusContainer = React.useCallback(() => {
		setState(state => ({ ...state, focused: true }));
	}, []);

	const onBlurContainer = React.useCallback(() => {
		setState(state => ({
			...state,
			focused: false,
			open: false,
			query: "",
			index: 0
		}));
	}, []);

	const onSelectValue = React.useCallback(
		(value: string | undefined) => {
			setState(state => ({ ...state, open: false, query: "", index: 0 }));
			onChangeValue(value);
		},
		[onChangeValue]
	);

	const onClearClick = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			onSelectValue(undefined);
		},
		[onSelectValue]
	);

	const onClickToggle = React.useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setState(state =>
			state.open
				? { ...state, focused: true, open: false }
				: { ...state, focused: true, open: true, query: "", index: 0 }
		);
	}, []);

	const onChangeQuery = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value: query } = event.target;
			setState(state => ({ ...state, query, index: 0 }));
		},
		[]
	);

	const onClickWindow = React.useCallback(
		(event: MouseEvent) => {
			if (containerRef.current?.contains(event.target as Element)) {
				return;
			}
			onBlurContainer();
		},
		[onBlurContainer]
	);

	const onKeyDownWindow = React.useCallback(
		(event: KeyboardEvent) => {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				event.stopPropagation();
				return setState(state =>
					state.open
						? {
								...state,
								index: Math.min(
									index + 1,
									enabledIndexes.length - 1
								)
							}
						: { ...state, open: true }
				);
			}
			if (event.key === "ArrowUp") {
				event.preventDefault();
				event.stopPropagation();
				return setState(state =>
					state.open
						? { ...state, index: Math.max(index - 1, 0) }
						: { ...state, open: true }
				);
			}
			if (event.key === "Enter") {
				event.preventDefault();
				event.stopPropagation();
				return onSelectValue(
					filteredOptions[enabledIndexes[index]].value
				);
			}
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopPropagation();
				return setState(state => ({
					...state,
					open: false,
					query: "",
					index: 0
				}));
			}
			if (event.key === "Tab") {
				return onBlurContainer();
			}
		},
		[filteredOptions, enabledIndexes, index, onSelectValue, onBlurContainer]
	);

	React.useEffect(() => {
		if (focused && open && queryRef.current !== null) {
			queryRef.current.focus();
		}
	}, [focused, open]);

	React.useEffect(() => {
		if (focused) {
			window.addEventListener("click", onClickWindow);
			window.addEventListener("keydown", onKeyDownWindow);
		} else {
			window.removeEventListener("click", onClickWindow);
			window.removeEventListener("keydown", onKeyDownWindow);
		}
		return () => {
			window.removeEventListener("click", onClickWindow);
			window.removeEventListener("keydown", onKeyDownWindow);
		};
	}, [focused, onClickWindow, onKeyDownWindow]);

	return (
		<div
			ref={containerRef}
			className={cs("select", {
				"select--disabled": disabled,
				"select--focused": focused,
				"select--open": open
			})}
			tabIndex={disabled ? -1 : 0}
			onMouseDown={onClickContainer}
			onFocus={onFocusContainer}
		>
			<div className="select__value">{selectedLabel}</div>
			<div className="select__indicators">
				<button
					type="button"
					disabled={!canClear || value === undefined || value === ""}
					tabIndex={-1}
					className="select__button select__button--clear"
					onMouseDown={onClearClick}
				>
					<FaTimes />
				</button>
				<button
					type="button"
					tabIndex={-1}
					className="select__button select__button--toggle"
					onMouseDown={onClickToggle}
				>
					<FaChevronDown />
				</button>
			</div>
			<div className="select__menu">
				<input
					ref={queryRef}
					type="text"
					className="select__query"
					placeholder={formatMessage(genericMessages.search)}
					value={query}
					onChange={onChangeQuery}
				/>
				<div className="select__options">
					{filteredOptions.length === 0 ? (
						<div className="select__no-options">
							<FormattedMessage {...genericMessages.noMatches} />
						</div>
					) : (
						filteredOptions.map((option, i) => (
							<SelectOption
								key={option.value}
								open={open}
								active={enabledIndexes[index] === i}
								selected={option.value === value}
								option={option}
								onSelect={onSelectValue}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

interface SelectOptionProps {
	open: boolean;
	active: boolean;
	selected: boolean;
	option: Option;
	onSelect(value: string): unknown;
}

const SelectOption: React.FunctionComponent<SelectOptionProps> = ({
	open,
	active,
	selected,
	option: { value, label, disabled },
	onSelect
}) => {
	const ref = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (!open || !active || ref.current === null) {
			return;
		}
		scrollIntoView(ref.current);
	}, [open, active]);

	const onClick = React.useCallback(() => {
		onSelect(value);
	}, [onSelect, value]);

	return (
		<div
			ref={ref}
			className={cs("select__option", {
				"select__option--active": active,
				"select__option--selected": selected,
				"select__option--disabled": disabled
			})}
			onClick={onClick}
		>
			{label}
		</div>
	);
};
